import React, { useEffect, useState } from 'react';
import {
  Button, TextField, FormControl, InputLabel, Select, MenuItem,
  FormControlLabel, Checkbox, Grid, Typography, Box,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import api from '../../api/client';
import { useSetRecoilState } from 'recoil';
import { loadingState } from '../../recoil/atoms';
import { toEnglishDigits } from '../../assets/utils';
import { toast } from '../../api/toast';

// فقط نقش‌هایی که دستی ساخته می‌شوند: کاربر سازمان و مدیر سازمان.
// پیمانکار/مشاور/ناظر از قرارداد می‌آیند و ادمین هم فعلاً دستی ساخته نمی‌شود.
const ROLES = [
  { value: 'Department', label: 'کاربر سازمان' },
  { value: 'DepartmentManager', label: 'مدیر سازمان' },
];

const ROLE_LABELS = {
  Department: 'کاربر سازمان',
  DepartmentManager: 'مدیر سازمان',
};

const onlyDigits = (v) => toEnglishDigits(String(v || '')).replace(/\D/g, '');

const AddUser = ({ setOpenAddUser, refreshUsers }) => {
  const setLoading = useSetRecoilState(loadingState);
  const [formData, setFormData] = useState({
    nationalCode: '',
    mobile: '',
    role: 'Department',
  });
  const [name, setName] = useState('');
  const [sendSms, setSendSms] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [departmentId, setDepartmentId] = useState('');
  const [personOptions, setPersonOptions] = useState([]);
  const [personValue, setPersonValue] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [existingAccount, setExistingAccount] = useState(false);

  const isDeptManager = formData.role === 'DepartmentManager';
  const needsOrg = true; // هر دو نقش به سازمان وصل می‌شوند

  // سازمان‌ها
  useEffect(() => {
    api.get('users/departments/').then((res) => {
      const list = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      setDepartments(list);
    }).catch(() => setDepartments([]));
  }, []);

  // جستجوی زنده در فهرست اشخاص
  useEffect(() => {
    const term = searchTerm.trim();
    if (term.length < 3) {
      setPersonOptions([]);
      setSearching(false);
      return undefined;
    }
    let alive = true;
    setSearching(true);
    const t = setTimeout(() => {
      api.get('users/admin/people/', { params: { q: term } })
        .then((res) => { if (alive) setPersonOptions((res.data && res.data.results) || []); })
        .catch(() => { if (alive) setPersonOptions([]); })
        .finally(() => { if (alive) setSearching(false); });
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [searchTerm]);

  const applyPerson = (person) => {
    setPersonValue(person);
    if (!person) return;
    setFormData((f) => ({
      ...f,
      nationalCode: onlyDigits(person.national_code),
      mobile: onlyDigits(person.mobile),
    }));
    setName(person.name || '');
    setExistingAccount(!!person.has_account);
  };

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData((f) => ({ ...f, [field]: value }));
  };

  const roleLabel = ROLE_LABELS[formData.role] || formData.role;
  const smsPreview = `ارجان پیمان: ${name || 'کاربر'} گرامی، در ArjanPeyman.ir برای شما حساب کاربری با نقش ${roleLabel} ایجاد شد. به سامانه مراجعه کنید.`;

  const handleSubmit = async () => {
    const nationalCode = onlyDigits(formData.nationalCode);
    const mobile = onlyDigits(formData.mobile);

    if (!/^\d{10,11}$/.test(nationalCode)) {
      toast('کد ملی باید ۱۰ رقم (شخص حقیقی) یا ۱۱ رقم (شخص حقوقی) باشد');
      return;
    }
    if (!/^09\d{9}$/.test(mobile)) {
      toast('شمارهٔ تلفن همراه باید با ۰۹ شروع شود و ۱۱ رقم باشد');
      return;
    }
    if (needsOrg && !departmentId) {
      toast('انتخابِ سازمان الزامی است');
      return;
    }
    if (existingAccount) {
      toast('برای این کد ملی قبلاً حساب ساخته شده است');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        national_code: nationalCode,
        name: name || '',
        mobile,
        role: formData.role,
        is_active: true,
        send_sms: sendSms,
      };
      if (needsOrg) payload.department_id = Number(departmentId);

      const res = await api.post('users/admin/users/', payload);

      if (sendSms) {
        if (res?.data?.sms_sent) toast('کاربر ساخته شد و پیامک ارسال شد', 'success');
        else toast('کاربر ساخته شد، اما پیامک ارسال نشد', 'warning');
      } else {
        toast('کاربر با موفقیت اضافه شد', 'success');
      }
      setOpenAddUser(false);
      if (refreshUsers) refreshUsers();
    } catch (error) {
      if (error.response?.status === 409) {
        toast('برای این کد ملی قبلاً حساب ساخته شده است');
      } else if (error.response?.data?.detail) {
        toast(error.response.data.detail);
      } else {
        toast('خطا در افزودن کاربر');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Autocomplete
          options={personOptions}
          value={personValue}
          loading={searching}
          filterOptions={(x) => x}
          getOptionLabel={(o) => (o ? `${o.name || '—'}${o.national_code ? ` — ${o.national_code}` : ''}` : '')}
          onChange={(e, v) => applyPerson(v)}
          onInputChange={(e, v, reason) => { if (reason === 'input') setSearchTerm(v); }}
          noOptionsText="شخصی پیدا نشد — کد ملی را دستی وارد کنید"
          loadingText="در حال جستجو…"
          renderOption={(o) => (
            <Box>
              <Typography style={{ fontWeight: 700, fontSize: 13.5 }}>
                {o.name || '—'}
                {o.has_account ? (
                  <span style={{ color: '#c62828', fontSize: 11.5, marginRight: 6 }}>(حساب دارد)</span>
                ) : null}
              </Typography>
              <Typography style={{ fontSize: 11.5, color: '#7a80a3' }} dir="ltr">
                {[o.national_code, o.mobile ? `0${onlyDigits(o.mobile)}` : '', o.organization].filter(Boolean).join('  |  ')}
              </Typography>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="جستجوی شخص در ارجان پیمان (نام، کد ملی، موبایل)"
              variant="outlined"
              fullWidth
              helperText="اگر شخص در فهرست باشد، با انتخابش کد ملی و موبایل خودکار پر می‌شود."
            />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          label="نام و نام خانوادگی"
          variant="outlined"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          helperText="در فهرست‌ها و متنِ پیامک نمایش داده می‌شود."
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          label="کد ملی / شناسهٔ ملی *"
          variant="outlined"
          fullWidth
          value={formData.nationalCode}
          onChange={handleChange('nationalCode')}
          inputProps={{ maxLength: 11, inputMode: 'numeric' }}
          error={existingAccount}
          helperText={existingAccount ? 'برای این کد ملی قبلاً حساب ساخته شده است.' : '۱۰ رقم (حقیقی) یا ۱۱ رقم (حقوقی)'}
          dir="ltr"
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          label="شمارهٔ تلفن همراه *"
          variant="outlined"
          fullWidth
          value={formData.mobile}
          onChange={handleChange('mobile')}
          placeholder="09123456789"
          helperText="ورود با کد یکبار مصرف روی همین شماره انجام می‌شود."
          inputProps={{ maxLength: 11, inputMode: 'tel' }}
          dir="ltr"
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl variant="outlined" fullWidth>
          <InputLabel>نقش *</InputLabel>
          <Select
            value={formData.role}
            onChange={handleChange('role')}
            label="نقش *"
          >
            {ROLES.map((r) => (
              <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl variant="outlined" fullWidth>
          <InputLabel>سازمان *</InputLabel>
          <Select
            value={departmentId}
            onChange={(e) => setDepartmentId(e.target.value)}
            label="سازمان *"
          >
            {departments.map((d) => (
              <MenuItem key={d.id} value={d.id}>
                {d.display_name || d.name || d.short_name || `#${d.id}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox checked={sendSms} onChange={(e) => setSendSms(e.target.checked)} color="primary" />
          }
          label="ارسال پیامک اطلاع‌رسانی به شمارهٔ بالا"
        />
        {sendSms && (
          <Typography variant="caption" style={{ display: 'block', color: '#7a80a3', lineHeight: 2 }}>
            {smsPreview}
          </Typography>
        )}
      </Grid>

      <Grid item xs={12}>
        <Button variant="contained" color="primary" onClick={handleSubmit} fullWidth>
          ذخیره
        </Button>
      </Grid>

      <Grid item xs={12}>
        <Button variant="outlined" onClick={() => setOpenAddUser(false)} fullWidth>
          انصراف
        </Button>
      </Grid>
    </Grid>
  );
};

export default AddUser;
